/* ============================================================
   <spring-curation> — filter pills + horizontal scroller + arrow nav
   The track is a native overflow-x scroller at every breakpoint
   (touchpad / horizontal wheel just work); the arrows scrollBy()
   one card at a time on the same scroll container.
   ============================================================ */

class SpringCuration extends HTMLElement {
  connectedCallback() {
    this.filters = Array.from(this.querySelectorAll('[js-spring-curation-filter]'));
    this.track = this.querySelector('[js-spring-curation-track]');
    this.arrows = this.querySelector('.spring-curation__arrows');
    this.prevBtn = this.querySelector('[js-spring-curation-prev]');
    this.nextBtn = this.querySelector('[js-spring-curation-next]');

    this.bindFilters();
    // Grid layout has no horizontal overflow and no arrows — skip the
    // scroller wiring entirely.
    if (this.track && this.dataset.layout !== 'grid') {
      this.bindArrows();
      this.bindScroll();
      this.bindKeyboard();
      this.updateArrows();
    }

    // Balance title + description to a constant line count (runs for both
    // scroller and grid layouts).
    this.balanceDescriptions();
    window.addEventListener('resize', () => this.scheduleBalance(), { passive: true });
    // Title height depends on the brand font; re-measure once it's loaded.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleBalance());
    }
  }

  // --- Title / description balancing ---------------------------
  // Each card keeps title + description at TOTAL_LINES total: a 1-line title
  // gets a 3-line description, 2 → 2, 3 → 1. The desc clamp is driven by the
  // --desc-lines custom property the CSS reads.

  scheduleBalance() {
    if (this._balanceRaf) cancelAnimationFrame(this._balanceRaf);
    this._balanceRaf = requestAnimationFrame(() => this.balanceDescriptions());
  }

  balanceDescriptions() {
    const TOTAL_LINES = 4;
    const cards = this.querySelectorAll('.spring-curation__card');
    cards.forEach((card) => {
      const title = card.querySelector('.spring-curation__card-title');
      // Skip cards whose description shows in full (event/agenda cards).
      const desc = card.querySelector(
        '.spring-curation__card-desc:not(.spring-curation__card-desc--full)'
      );
      if (!title || !desc) return;

      const styles = window.getComputedStyle(title);
      let lineHeight = parseFloat(styles.lineHeight);
      if (!lineHeight || Number.isNaN(lineHeight)) {
        // line-height: normal → estimate from font-size.
        lineHeight = parseFloat(styles.fontSize) * 1.2;
      }
      const titleLines = Math.max(1, Math.round(title.offsetHeight / lineHeight));
      const descLines = Math.max(1, TOTAL_LINES - titleLines);
      desc.style.setProperty('--desc-lines', descLines);
    });
  }

  // --- Filters -------------------------------------------------

  bindFilters() {
    this.filters.forEach((btn, idx) => {
      btn.addEventListener('click', () => {
        this.activateFilter(btn.getAttribute('data-filter-target'));
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.activateFilter(btn.getAttribute('data-filter-target'));
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const dir = e.key === 'ArrowRight' ? 1 : -1;
          const next = (idx + dir + this.filters.length) % this.filters.length;
          this.filters[next].focus();
        }
      });
    });
  }

  activateFilter(target) {
    if (!target) return;

    this.setAttribute('data-active-filter', target);

    this.filters.forEach((btn) => {
      const isActive = btn.getAttribute('data-filter-target') === target;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Key-agnostic show/hide: a card is visible when "all" is active or its
    // data-item-type matches the active filter. Works for both the fixed
    // product/experience/event keys and arbitrary collection-group ids.
    if (this.track) {
      const cards = this.track.querySelectorAll('.spring-curation__card');
      cards.forEach((card) => {
        const show = target === 'all' || card.dataset.itemType === target;
        card.classList.toggle('is-filtered-out', !show);
      });

      this.track.scrollLeft = 0;
      // Allow the CSS hide/show to settle before measuring scroll bounds.
      requestAnimationFrame(() => this.updateArrows());
    }
  }

  // --- Arrow nav -----------------------------------------------

  bindArrows() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByDirection(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByDirection(1));
    }
  }

  bindScroll() {
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { passive: true });
  }

  bindKeyboard() {
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollByDirection(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollByDirection(-1);
      }
    });
  }

  getFirstVisibleCard() {
    // First card whose computed display is not 'none' — respects filter state.
    const cards = this.track.querySelectorAll('.spring-curation__card');
    for (const card of cards) {
      if (window.getComputedStyle(card).display !== 'none') return card;
    }
    return null;
  }

  scrollByDirection(direction) {
    if (!this.track) return;
    const card = this.getFirstVisibleCard();
    if (!card) return;

    const cardWidth = card.getBoundingClientRect().width;
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    const step = cardWidth + gap;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: step * direction,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateArrows() {
    if (!this.prevBtn && !this.nextBtn) return;
    if (!this.track) return;

    const atStart = this.track.scrollLeft <= 1;
    const atEnd =
      this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;

    // Hide the arrows entirely when every card already fits (no overflow) —
    // empty string restores the stylesheet's own breakpoint visibility.
    if (this.arrows) this.arrows.style.display = hasOverflow ? '' : 'none';

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }
}

if (!customElements.get('spring-curation')) {
  customElements.define('spring-curation', SpringCuration);
}
